<template>
  <v-app>
    <v-app-bar app flat color="white" class="px-4 topbar-border">
      <img :src="logoSrc" alt="Hublot" height="38" />
      <v-spacer />
      <v-chip color="primary" dark class="font-weight-bold px-4" label>
        <v-icon left small>assignment</v-icon>
        GLN du magasin
      </v-chip>
    </v-app-bar>

    <v-main class="page-bg">
      <v-container class="py-8 gln-container">

        <!-- CHARGEMENT -->
        <v-card v-if="loading" class="gln-card pa-6" elevation="0">
          <v-skeleton-loader type="list-item-avatar-two-line" class="mb-4" />
          <v-skeleton-loader type="paragraph" class="mb-4" />
          <v-skeleton-loader type="image" height="56" />
        </v-card>

        <!-- MAGASIN INTROUVABLE -->
        <v-card v-else-if="notFound" class="gln-card pa-8 text-center" elevation="0">
          <v-icon size="64" color="error" class="mb-4">error_outline</v-icon>
          <div class="text-h6 mb-2">Magasin introuvable</div>
          <div class="grey--text">Ce lien ne correspond à aucun magasin. Vérifiez le lien reçu ou contactez Hublot.</div>
        </v-card>

        <!-- SERVICE INDISPONIBLE -->
        <v-card v-else-if="serviceDown" class="gln-card pa-8 text-center" elevation="0">
          <v-icon size="64" color="warning" class="mb-4">cloud_off</v-icon>
          <div class="text-h6 mb-2">Service momentanément indisponible</div>
          <div class="grey--text mb-4">Nous n'avons pas pu charger les informations de votre magasin.</div>
          <v-btn color="primary" outlined @click="load">
            <v-icon left small>refresh</v-icon>
            Réessayer
          </v-btn>
        </v-card>

        <!-- FORMULAIRE -->
        <v-card v-else class="gln-card" elevation="0">
          <div class="gln-accent" />
          <v-card-text class="pa-6">
            <div class="d-flex align-center mb-5">
              <v-avatar color="primary" size="44" class="mr-3">
                <v-icon dark>store</v-icon>
              </v-avatar>
              <div>
                <div class="store-name">{{ store.enseigne }} {{ store.name }}</div>
                <v-chip v-for="metier in metiers" :key="metier" x-small color="secondary" dark class="mt-1 mr-1">
                  {{ metier | capitalize }}
                </v-chip>
              </div>
            </div>

            <div class="gln-heading mb-2">{{ mode.heading }}</div>
            <p v-if="mode.key === 'leclerc'" class="gln-text mb-4">
              Il se construit <b>SIRET_GLN</b> : le SIRET de votre société (14 chiffres ; le SIREN à 9 chiffres
              est aussi accepté), un tiret bas, puis le GLN (Global Location Number, 13 chiffres) de votre
              magasin. Par exemple {{ mode.example }}.
              Si vous ne le connaissez pas, votre centrale ou votre service comptabilité peut vous le communiquer.
            </p>
            <p v-else class="gln-text mb-4">
              Selon votre enseigne (Intermarché, Super U…), il s'agit du <b>code de routage</b> de facturation
              électronique communiqué par votre centrale, ou à défaut du <b>SIREN</b> de votre société (9 chiffres).
              Si vous ne le connaissez pas, votre centrale ou votre service comptabilité peut vous le communiquer.
            </p>

            <v-alert v-if="savedGln" type="success" text dense class="mb-4">
              Merci, {{ savedGln }} est enregistré. Vous pouvez fermer cette page.
            </v-alert>
            <v-alert v-else-if="hasConflict" type="warning" text dense class="mb-4">
              Nous avons deux identifiants différents pour votre magasin :
              <span v-for="knownStore in storesWithGln" :key="knownStore.metier" class="d-block">
                {{ knownStore.gln }} ({{ knownStore.metier }})
              </span>
              Indiquez le bon, puis enregistrez.
            </v-alert>
            <v-alert v-else-if="suggestion" type="info" text dense class="mb-4">
              Nous avons déjà cet identifiant pour votre magasin. Vérifiez-le, corrigez-le si besoin, puis enregistrez.
            </v-alert>

            <v-form ref="glnForm" @submit.prevent="save">
              <v-text-field
                v-model="gln"
                :label="mode.label"
                :hint="mode.hint"
                :placeholder="mode.example"
                persistent-hint
                inputmode="text"
                autocomplete="off"
                maxlength="30"
                :counter="mode.counter"
                :counter-value="countDigits"
                :rules="glnRules"
                :disabled="isSaving"
                outlined
                class="gln-input"
              />
              <v-row justify="end" class="mt-2">
                <v-col cols="auto">
                  <v-btn type="submit" color="primary" large :disabled="!canSave" :loading="isSaving">
                    <v-icon left>save</v-icon>
                    Enregistrer
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>

      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import GlnService from '@/services/GlnService'
import poissonnerieConfig from '@/config/metiers/poissonnerie'
import boucherieConfig from '@/config/metiers/boucherie'
import { cleanGln, glnModeFor } from '@/utils/glnUtils'

const METIER_CONFIGS = { poissonnerie: poissonnerieConfig, boucherie: boucherieConfig }

const LOGOS = {
  boucherie: require('@/assets/long-boucherie.png'),
  poissonnerie: require('@/assets/logo_poissonnerie.png'),
}

export default {
  name: 'Gln',
  props: {
    idNotion: { type: String, default: '' },
  },
  data () {
    return {
      loading: true,
      stores: [],
      gln: '',
      savedGln: '',
      isSaving: false,
    }
  },
  filters: {
    capitalize: function (value) {
      return value[0].toUpperCase() + value.slice(1)
    },
  },
  created () {
    document.title = 'GLN du magasin - Hublot'
    this.load()
  },
  methods: {
    load () {
      this.loading = true
      return this.fetchStores()
        .then((stores) => {
          this.stores = stores
          this.gln = this.suggestion
          this.applyMetierTheme()
        })
        .finally(() => {
          this.loading = false
          this.$nextTick(this.checkSuggestion)
        })
    },
    // Le lien du mail porte son rayon : la page ne montre et n'écrit que cet univers
    fetchStores () {
      if (!this.rayon) {
        return GlnService.get(this.idNotion)
      }
      return GlnService.getOne(this.rayon, this.idNotion).then((store) => (store ? [store] : []))
    },
    // Un GLN déjà en base mais mal formé doit s'afficher en erreur sans attendre une frappe
    checkSuggestion () {
      if (this.suggestion && this.$refs.glnForm) {
        this.$refs.glnForm.validate()
      }
    },
    save () {
      if (!this.canSave) {
        return
      }
      const gln = cleanGln(this.gln)
      this.isSaving = true
      GlnService.save(this.idNotion, this.metiers, gln)
        .then((results) => {
          if (results.every((result) => result.saved)) {
            this.savedGln = gln
            this.$toast.success('Identifiant de facturation enregistré')
          } else {
            this.savedGln = ''
            this.$toast.error('L\'enregistrement a échoué, merci de réessayer')
          }
        })
        .finally(() => (this.isSaving = false))
    },
    countDigits (value) {
      return cleanGln(value).length
    },
    applyMetierTheme () {
      const theme = this.$vuetify.theme.themes.light
      theme.primary = this.metier.theme.primary
      theme.secondary = this.metier.theme.secondary
    },
  },
  computed: {
    rayon () {
      const rayon = this.$route.query.rayon
      return METIER_CONFIGS[rayon] ? rayon : ''
    },
    // Un univers qui ne répond pas bloque la saisie : on n'enregistre pas le GLN à moitié
    serviceDown () {
      return this.stores.some((store) => store.unavailable)
    },
    notFound () {
      return this.stores.length === 0
    },
    store () {
      return this.stores[0] || {}
    },
    metiers () {
      return this.stores.map((store) => store.metier)
    },
    metier () {
      return METIER_CONFIGS[this.metiers[0]] || poissonnerieConfig
    },
    logoSrc () {
      return LOGOS[this.metier.key] || LOGOS.poissonnerie
    },
    storesWithGln () {
      return this.stores.filter((store) => !!store.gln)
    },
    suggestion () {
      return this.storesWithGln.length > 0 ? this.storesWithGln[0].gln : ''
    },
    hasConflict () {
      return this.storesWithGln.some((store) => store.gln !== this.suggestion)
    },
    // Leclerc attend le SIRET_GLN historique, les autres enseignes un code de routage ou un SIREN
    mode () {
      return glnModeFor(this.store.enseigne)
    },
    glnRules () {
      return [
        (value) => !!cleanGln(value) || 'Requis.',
        (value) => this.mode.isValid(value) || `Format attendu : ${this.mode.hint} (ex. ${this.mode.example})`,
      ]
    },
    canSave () {
      return this.mode.isValid(this.gln) && !this.isSaving
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/vars.scss';

.topbar-border {
  border-bottom: 2px solid var(--v-secondary-base) !important;
}
.page-bg {
  background: #f4f7f9;
}
.gln-container {
  max-width: 640px;
}
.gln-card {
  border-radius: 14px !important;
  border: 1px solid #dde5ea !important;
  background: white;
  overflow: hidden;
  position: relative;
}
.gln-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--v-primary-base), var(--v-secondary-base));
}
.store-name {
  font-weight: 700;
  font-size: 1.05rem;
  color: $greyText;
}
.gln-heading {
  font-weight: 700;
  font-size: 1.15rem;
  color: var(--v-primary-base);
}
.gln-text {
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.6);
}
.gln-input {
  font-size: 1.2rem;
  letter-spacing: 0.08em;
}
</style>
